import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import type { PatchNote } from '../../../types/patchNote';

const categoryColor: Record<string, string> = {
  Warzone: 'text-[#ff6b35] border-[#ff6b35]/50',
  Multiplayer: 'text-[#00ff41] border-[#00ff41]/50',
  Zombies: 'text-purple-400 border-purple-400/50',
};

export default function PatchNotePreview() {
  const [patches, setPatches] = useState<PatchNote[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('patch_notes')
        .select('id, version, title, category, highlight, date')
        .eq('published', true)
        .order('date', { ascending: false })
        .limit(3);
      setPatches((data as PatchNote[]) ?? []);
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <section className="bg-[#0d0d0d] py-12 border-t border-[#1e1e1e]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 border-b border-[#1e1e1e] pb-4">
          <h2 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase tracking-widest">
            Latest Patch Notes
          </h2>
          <Link
            to="/patch-notes"
            className="font-['Barlow_Condensed'] font-bold text-xs tracking-widest text-[#888] hover:text-white border border-[#333] px-4 py-2 hover:border-[#666] transition-all cursor-pointer whitespace-nowrap"
          >
            ALL PATCHES
          </Link>
        </div>

        {/* Skeleton */}
        {loading && (
          <div className="divide-y divide-[#1e1e1e] animate-pulse">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-6 py-5">
                <div className="shrink-0 w-20 space-y-1.5">
                  <div className="h-6 w-14 bg-[#1e1e1e] rounded mx-auto" />
                  <div className="h-3 w-16 bg-[#1a1a1a] rounded mx-auto" />
                </div>
                <div className="w-px h-10 bg-[#222] shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-[#1e1e1e] rounded" />
                  <div className="h-3 w-2/3 bg-[#1a1a1a] rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Patch list */}
        {!loading && patches.length > 0 && (
          <div className="divide-y divide-[#1e1e1e]">
            {patches.map((patch) => (
              <Link key={patch.id} to="/patch-notes" className="group flex items-center gap-6 py-5 cursor-pointer">
                {/* Version */}
                <div className="shrink-0 w-20 text-center">
                  <div className="font-['Barlow_Condensed'] font-black text-white text-2xl leading-none">
                    {patch.version}
                  </div>
                  <div className="text-[#555] text-xs mt-1">
                    {new Date(patch.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>

                <div className="w-px h-10 bg-[#222] shrink-0" />

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className={`text-xs font-['Barlow_Condensed'] font-bold tracking-widest border px-2 py-0.5 shrink-0 ${categoryColor[patch.category] ?? 'text-[#888] border-[#444]'}`}>
                      {patch.category.toUpperCase()}
                    </span>
                    <h3 className="font-['Barlow_Condensed'] font-bold text-white text-base group-hover:text-[#00ff41] transition-colors truncate">
                      {patch.title}
                    </h3>
                  </div>
                  <p className="text-[#666] text-sm truncate">{patch.highlight}</p>
                </div>

                <div className="w-6 h-6 flex items-center justify-center text-[#555] group-hover:text-[#00ff41] transition-colors shrink-0">
                  <i className="ri-arrow-right-s-line text-lg" />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && patches.length === 0 && (
          <div className="text-center py-10 text-[#444] text-sm font-['Barlow_Condensed']">
            No patch notes published yet.
          </div>
        )}
      </div>
    </section>
  );
}
